import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { analyzeKeywords, subscribeAnalyses } from '../services/games';
import {
  LANGUAGE_LABELS,
  type AiAnalysis,
  type AnalyzeScope,
  type SupportedLanguage,
} from '../types';

function formatDate(v: AiAnalysis['createdAt']): string {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v.seconds * 1000);
  return d.toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Markdown result view. Images are disallowed: the model reads third-party
 *  data (Google suggestions), so a rendered image would be an outbound
 *  request channel to whatever URL a poisoned suggestion talks it into. */
function ResultMarkdown({ text }: { text: string }) {
  return (
    <div className="analysis-result">
      <ReactMarkdown remarkPlugins={[remarkGfm]} disallowedElements={['img']}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Free-form AI analysis over the latest run's keyword data. Deliberately a
 * bare prompt box: the goal of v1 is prompt exploration — find what's worth
 * productizing — so every execution is logged and reusable from the history
 * panel.
 */
export function AiAnalyzePage() {
  const { gameId, language } = useParams<{ gameId: string; language: string }>();
  const lang = language as SupportedLanguage | undefined;

  const [prompt, setPrompt] = useState('');
  const [scope, setScope] = useState<AnalyzeScope>('ranked');
  // Fire-and-forget like 今すぐ実行: we don't await the callable. `awaitingSince`
  // marks the newest log id at kick time — when a newer one arrives via the
  // live subscription, the "in progress" notice clears itself.
  const [awaitingSince, setAwaitingSince] = useState<string | null>(null);
  const [analyses, setAnalyses] = useState<AiAnalysis[]>([]);
  // Set when the history subscription itself dies (e.g. composite index
  // missing). It is the only channel that shows results and failures, so its
  // breakage must be visible, not an empty history.
  const [subError, setSubError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId || !lang) return;
    // Reset per (game, language): without this, switching the URL param shows
    // the previous language's rows until the new snapshot arrives, and a
    // stale awaitingSince can surface the old wait notice.
    setAnalyses([]);
    setAwaitingSince(null);
    setSubError(null);
    return subscribeAnalyses(gameId, lang, setAnalyses, (err) => setSubError(err.message));
  }, [gameId, lang]);

  if (!gameId || !lang) return null;

  const waiting = awaitingSince !== null && (analyses[0]?.id ?? 'none') === awaitingSince;

  const run = () => {
    if (!prompt.trim()) return;
    // Fire-and-forget: the long-running analysis writes its result to
    // Firestore, which the history panel below picks up live. Errors land in
    // Cloud Functions logs (same contract as triggerRun).
    analyzeKeywords(gameId, lang, prompt.trim(), scope).catch((err) => {
      console.error('analyzeKeywords failed', err);
    });
    setAwaitingSince(analyses[0]?.id ?? 'none');
  };

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>AI分析（{LANGUAGE_LABELS[lang] ?? lang}）</h2>
        <Link to={`/games/${gameId}`} className="btn-secondary" style={{ padding: '7px 13px' }}>
          ← ゲーム詳細に戻る
        </Link>
      </div>

      <div className="card">
        <div className="form-row">
          <label>プロンプト（キーワードデータはシステム側で自動的に添付されます）</label>
          <textarea
            rows={5}
            value={prompt}
            maxLength={4000}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="例: ツール（攻略記事・機能）に活かせそうなキーワードを重要度順に10件挙げ、それぞれ理由と想定コンテンツを添えてください"
          />
          {/* The backend rejects >4000 chars before anything is logged, so an
              over-long prompt would fail invisibly — block it at input time. */}
          {prompt.length >= 3500 && (
            <div className="muted" style={{ textAlign: 'right' }}>{prompt.length} / 4000</div>
          )}
        </div>
        <div className="form-row">
          <label>対象範囲</label>
          <label style={{ fontWeight: 'normal' }}>
            <input type="radio" name="scope" checked={scope === 'ranked'} onChange={() => setScope('ranked')} />
            {' '}選別済みキーワードのみ（順位・スコア・前日比つき）
          </label>
          <label style={{ fontWeight: 'normal' }}>
            <input type="radio" name="scope" checked={scope === 'all'} onChange={() => setScope('all')} />
            {' '}全取得キーワードを含める（AIフィルタ前の生サジェスト全量・文字列のみで順位データなし）
          </label>
        </div>
        <div className="row">
          <button className="primary" onClick={run} disabled={!prompt.trim()}>
            AI分析を実行
          </button>
          {waiting && (
            <span className="muted">
              分析を開始しました。完了すると下の実行履歴に結果が表示されます（数分かかることがあります）。
            </span>
          )}
        </div>
      </div>

      <h3 style={{ marginBottom: 8 }}>実行履歴</h3>
      {subError ? (
        <div className="card">
          <span className="error-text">実行履歴の取得に失敗しました: {subError}</span>
        </div>
      ) : analyses.length === 0 ? (
        <div className="empty card">この言語での実行履歴はまだありません。</div>
      ) : (
        <div className="card">
          {analyses.map((a) => (
            <details key={a.id} className="analysis-log">
              <summary>
                <span className="muted">{formatDate(a.createdAt)}</span>
                {' '}
                <span className="tag">{a.scope === 'all' ? '全取得' : '選別済み'}</span>
                {a.isError && <span className="tag tag-error">エラー</span>}
                {' '}
                {a.prompt.length > 60 ? `${a.prompt.slice(0, 60)}…` : a.prompt}
              </summary>
              <div className="analysis-log-body">
                <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                  model: {a.model} / キーワード{a.keywordCount}件
                  {a.suggestionCount > 0 && ` + 生サジェスト${a.suggestionCount}件`} / {(a.durationMs / 1000).toFixed(1)}秒
                  {'　'}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setPrompt(a.prompt);
                      setScope(a.scope);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    このプロンプトを再利用
                  </button>
                </div>
                <ResultMarkdown text={a.resultText} />
              </div>
            </details>
          ))}
        </div>
      )}
    </>
  );
}
